import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import { Page } from '../../components/page';
import {
  Feature,
  PlanInterval,
  SubscriptionSource,
} from '../../models';
import { useQuery } from '../../routes';
import { fromOrder, fromSubscription, useAppDispatch } from '../../store';
import { TrueBundlePlanSelect } from './component/true-platform/TrueBundlePlanSelect';
import { OrderSummary } from './component/true-platform/OrderSummary';
import { LoadingProgress } from '../../layout';
import { Panel } from '../../components/common/Panel';
import { usePreviewTrue } from '../../hook';
import {
  getCycleFromPathParam,
  getPlanProductCycleFromPathParam,
} from '../../utils/utils';
import OrderDetailSection from './component/OrderDetailSection';
import { PlanStepNav } from './component/PlanStepNav';
import { DEFAULT_TRUE_BUNDLE_FEATURE } from './component/true-platform/trueBundlePlans';
import { useStarterLayoutState } from './component/true-platform/useStarterBundlePlans';

const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

export const PlanV2 = () => {
  const { get } = useQuery();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const legacyTrueSubscription = trueSubscriptions.find(
    sub => sub.feature === Feature.TRUE_TEAM
      || sub.feature === Feature.TRUE_ENTERPRISE,
  );
  const subscription = legacyTrueSubscription ?? trueSubscriptions[0];
  // R6 (Decision 16): the True Platform / True Automation cycle params no longer win here — those
  // rows are retired from self-serve, so an old pricing-page link that pins their cycle is ignored.
  const defaultTrueBundleCycle = getCycleFromPathParam(DEFAULT_TRUE_BUNDLE_FEATURE);
  const selectedProductCycle = getPlanProductCycleFromPathParam();
  const cycle = defaultTrueBundleCycle ?? selectedProductCycle;
  const [billingInterval, setBillingInterval] = useState<PlanInterval>(
    subscription?.billingInterval ?? PlanInterval.YEAR,
  );
  // A Starter-eligible account (new account + a Starter on sale) picks a plan from the Starter
  // cards; everyone else adds seats to the plan table. While `pending` the verdict is in flight,
  // so the heading holds a skeleton instead of flashing the wrong title.
  const {
    plans: starterBundlePlans,
    pending: starterLayoutPending,
  } = useStarterLayoutState(accountId);
  const isStarterLayout = starterBundlePlans.length > 0;
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPendingOrder = !!pendingOrder && pendingOrder.length > 0;
  const isAnnual = billingInterval === PlanInterval.YEAR;
  const disabledActionSx = hasPendingOrder
    ? { pointerEvents: 'none' as const, opacity: 0.5 }
    : undefined;

  useEffect(() => {
    dispatch(fromOrder.setAddOns({}));
  }, [dispatch]);

  const loading = useSelector(fromOrder.selectLoading);

  useEffect(() => {
    if (cycle && (!subscription || subscription.billingInterval === PlanInterval.MONTH)) {
      setBillingInterval(cycle);
    }
  }, [cycle]);

  const fetchData = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetOrderPreviewRequest()),
        dispatch(fromOrder.resetOrderPreviewResponse()),
      ]);
    }
  };

  usePreviewTrue();

  useEffect(() => {
    dispatch(fromOrder.doFetchAllOrder(accountId));
  }, [accountId, dispatch]);

  return (
    <Page
      id="plan-v2-page"
      key="plan-v2-page"
      headerId="page.plan.v2.header"
      fetchData={fetchData}
      background="#F7F9F8"
      hideHeader
    >
      <PlanStepNav currentStep={1} />
      {/* Temporarily hidden — restore by re-adding <LimitedOfferBanner /> */}
      {pendingOrder && pendingOrder.length > 0
        && (
          <Box
            id="expand-plan-banner"
            display="flex"
            mt={2}
            border="1px solid #dfbc4a"
            borderRadius={2}
            maxWidth={1825}
          >
            <OrderDetailSection pendingOrder={pendingOrder[0]} />
          </Box>
        )}
      <Box id="plan-v2-heading" pt={4}>
        <Typography
          sx={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: 30,
            fontWeight: 500,
            letterSpacing: '-.02em',
            color: '#16141d',
          }}
        >
          {starterLayoutPending ? (
            <Skeleton variant="text" width={340} sx={{ display: 'inline-block' }} />
          ) : (
            <FormattedMessage
              id={isStarterLayout
                ? 'page.plan.v2.starter.heading'
                : 'page.plan.v2.true.bundle.add.seats.title'}
            />
          )}
        </Typography>
      </Box>
      <Box
        id="plan-v2-content"
        key="plan-v2-content"
        display="flex"
        py={3}
        sx={theme => ({
          flexDirection: 'row',
          [theme.breakpoints.down('tabletL')]: {
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        })}
      >
        {/* Left panel */}
        <Panel
          id="plan-v2"
          transparent
          sx={{
            width: '100%',
            minWidth: '70%',
            maxWidth: 1200,
            pr: 4,
            pb: 4,
            borderRight: 'none',
          }}
        >
          <Box px={0}>
            <Box sx={disabledActionSx}>
              {/* Number of seats */}
              <TrueBundlePlanSelect />
            </Box>
          </Box>
        </Panel>

        {/* Right panel — Order Summary */}
        <Box
          id="plan-v2-right"
          sx={theme => ({
            width: '30%',
            flexShrink: 0,
            [theme.breakpoints.down('tabletL')]: {
              width: '100%',
            },
          })}
        >
          <OrderSummary
            isAnnual={isAnnual}
            groupByInterval
            showCoupon
          />
        </Box>
      </Box>
      {loading && (<LoadingProgress fullscreen />)}
    </Page>
  );
};
